import React, { useCallback, useEffect, useState } from 'react';
import { Target, Plus, Trash2, Star, Pencil, Sparkles } from 'lucide-react';
import { researchAPI } from '../services/api';
import { CRITERIA_MAX, profileToForm, formToPayload, criteriaFromText } from '../utils/research';
import useConfirm from '../hooks/useConfirm';
import toast from '../utils/toast';

const EMPTY = { name: '', description: '', criteriaText: '' };

const errorText = (err) => err?.response?.data?.error || err?.message || 'Something went wrong';

const ResearchProfilesCard = () => {
  const [confirm, confirmDialog] = useConfirm();
  const [profiles, setProfiles] = useState(null); // null = loading
  const [editingId, setEditingId] = useState(null); // null = list, 'new' = new profile, number = editing
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [brief, setBrief] = useState('');
  const [generating, setGenerating] = useState(false);
  const [drafted, setDrafted] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await researchAPI.listProfiles();
      setProfiles(res.data.profiles || []);
    } catch (err) {
      setProfiles([]);
      setError(errorText(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => { setForm(EMPTY); setDrafted(false); setEditingId('new'); setError(''); };
  const startAI = () => { setBrief(''); setEditingId('ai'); setError(''); };
  const startEdit = (p) => { setForm(profileToForm(p)); setDrafted(false); setEditingId(p.id); setError(''); };

  const generate = async (e) => {
    e.preventDefault();
    setGenerating(true);
    setError('');
    try {
      const res = await researchAPI.generateProfile(brief.trim());
      setForm(profileToForm(res.data.profile));
      setDrafted(true);
      setEditingId('new');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setGenerating(false);
    }
  };
  const cancel = () => { setEditingId(null); setError(''); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = formToPayload(form);
      if (editingId === 'new') await researchAPI.createProfile(payload);
      else await researchAPI.updateProfile(editingId, payload);
      setEditingId(null);
      await load();
      toast.success('Profile saved.');
    } catch (err) {
      // 409 = duplicate name: the backend sends its own message, with a fallback
      if (err?.response?.status === 409) {
        setError(err.response.data?.error || 'You already have a profile with that name.');
      } else {
        setError(errorText(err));
      }
    } finally {
      setSaving(false);
    }
  };

  const makeDefault = async (id) => {
    try { await researchAPI.setDefaultProfile(id); await load(); } catch (err) { toast.error(errorText(err)); }
  };

  const remove = async (p) => {
    const ok = await confirm({
      title: 'Delete profile',
      message: `Delete the profile "${p.name}"? Results already saved keep its name.`,
      confirmText: 'Delete',
    });
    if (!ok) return;
    try { await researchAPI.deleteProfile(p.id); await load(); } catch (err) { toast.error(errorText(err)); }
  };

  const criteriaCount = criteriaFromText(form.criteriaText).length;
  const tooMany = criteriaCount > CRITERIA_MAX;

  return (
    <div id="research-profiles" className="settings-card">
      <div className="settings-card-head">
        <span className="settings-card-icon"><Target size={17} /></span>
        <div className="settings-card-titles">
          <h3>Research profiles</h3>
          <p>Describe who a good prospect is. AI research scores each prospect against one of these. Create one per campaign or niche.</p>
        </div>
      </div>

      {profiles === null && <p className="setting-note">Loading...</p>}

      {editingId === null && profiles !== null && (
        <>
          {profiles.length === 0 && (
            <p className="setting-note">No profiles yet. Let AI draft one from a short description, or write your own.</p>
          )}
          {profiles.length > 0 && (
            <ul className="research-profile-list">
              {profiles.map((p) => (
                <li key={p.id} className="research-profile-item">
                  <div className="research-profile-main">
                    <strong>{p.name}</strong>
                    {p.is_default && <span className="settings-pill-success">Default</span>}
                    <div className="research-profile-meta">{(p.criteria || []).length} criteria</div>
                  </div>
                  <div className="research-profile-actions">
                    {!p.is_default && (
                      <button type="button" className="research-profile-btn" onClick={() => makeDefault(p.id)} title="Use this profile by default">
                        <Star size={14} /> Make default
                      </button>
                    )}
                    <button type="button" className="research-profile-btn" onClick={() => startEdit(p)}>
                      <Pencil size={14} /> Edit
                    </button>
                    <button type="button" className="research-profile-btn research-profile-btn-danger" onClick={() => remove(p)} aria-label={`Delete ${p.name}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {error && <small className="setting-error" role="alert">{error}</small>}
          <div className="settings-card-foot">
            <button type="button" className="test-button" onClick={startAI}>
              <Sparkles size={14} /> Generate with AI
            </button>
            <button type="button" className="card-save-button" onClick={startNew}>
              <Plus size={14} /> New profile
            </button>
          </div>
        </>
      )}

      {editingId === 'ai' && (
        <form onSubmit={generate} className="research-profile-form">
          <label htmlFor="rp-brief">What do you sell, and who do you want to reach?</label>
          <textarea id="rp-brief" rows={4} maxLength={1000} value={brief} required
            onChange={(e) => setBrief(e.target.value)}
            placeholder="e.g. I build websites for plumbers and heating engineers in the UK who rely on word of mouth." />
          <small className="setting-help">
            AI drafts a profile from this. You can edit it before it is saved. This uses your own OpenRouter key.
          </small>
          {error && <small className="setting-error" role="alert">{error}</small>}
          <div className="settings-card-foot">
            <button type="button" className="test-button" onClick={cancel} disabled={generating}>Cancel</button>
            <button type="submit" className="card-save-button" disabled={generating || !brief.trim()}>
              <Sparkles size={14} /> {generating ? 'Drafting...' : 'Draft profile'}
            </button>
          </div>
        </form>
      )}

      {editingId !== null && editingId !== 'ai' && (
        <form onSubmit={save} className="research-profile-form">
          {drafted && (
            <small className="setting-help">Drafted by AI. Check it and change anything before you save.</small>
          )}
          <label htmlFor="rp-name">Name</label>
          <input id="rp-name" type="text" maxLength={80} value={form.name} required
            onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Web design - plumbers" />
          <label htmlFor="rp-desc">Who is your ideal customer?</label>
          <textarea id="rp-desc" rows={4} maxLength={1000} value={form.description} required
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="e.g. Plumbing and heating firms in the UK with an outdated website and no online booking." />
          <label htmlFor="rp-criteria">Criteria (one per line, up to {CRITERIA_MAX})</label>
          <textarea id="rp-criteria" rows={6} value={form.criteriaText}
            onChange={(e) => setForm({ ...form, criteriaText: e.target.value })}
            placeholder={'Website looks outdated\nNo online booking\nServes the Bath area'} />
          <small className={tooMany ? 'setting-error' : 'setting-help'}>
            {criteriaCount}/{CRITERIA_MAX} criteria.
            {tooMany
              ? ' Remove some to save.'
              : ' The score is the share of criteria the AI can back up with evidence from the prospect’s own website.'}
          </small>
          {error && <small className="setting-error" role="alert">{error}</small>}
          <div className="settings-card-foot">
            <button type="button" className="test-button" onClick={cancel} disabled={saving}>Cancel</button>
            <button type="submit" className="card-save-button" disabled={saving || tooMany || criteriaCount === 0}>
              {saving ? 'Saving...' : 'Save profile'}
            </button>
          </div>
        </form>
      )}
      {confirmDialog}
    </div>
  );
};

export default ResearchProfilesCard;
